import type { ViewCallResult, ViewInput, ViewMeta, ViewStep } from '@deskworks/view-runtime';
import { t } from '@/lib/i18n';
import type { WorkerRequest, WorkerResponse } from './view-worker';

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

/** Error from the view's own code (compile, load, render or event). */
export class ViewRunError extends Error {
  constructor(
    message: string,
    readonly phase?: string,
  ) {
    super(message);
  }
}

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

/** The one Web Worker of this tab that runs every open view. */
function getWorker() {
  if (worker) return worker;
  worker = new Worker(new URL('./view-worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const res = event.data;
    const p = pending.get(res.id);
    if (!p) return;
    pending.delete(res.id);
    if (res.ok) p.resolve(res.value);
    else p.reject(new ViewRunError(res.error, res.phase));
  };
  worker.onerror = (event) => {
    for (const p of pending.values()) p.reject(new ViewRunError(event.message || t('The view engine stopped.')));
    pending.clear();
    worker = null;
  };
  return worker;
}

type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;

function send<T>(req: WithoutId<WorkerRequest>): Promise<T> {
  const id = nextId++;
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
    getWorker().postMessage({ ...req, id } as WorkerRequest);
  });
}

/** One open view in the engine; `key` is unique per mounted view. */
export const viewEngine = {
  load: (key: string, code: string) => send<ViewMeta>({ type: 'load', key, code }),
  render: (key: string, input: ViewInput) => send<ViewStep>({ type: 'render', key, input }),
  event: (key: string, handler: string, arg?: unknown) => send<ViewStep>({ type: 'event', key, handler, arg }),
  settle: (key: string, call: number, result: ViewCallResult) => send<ViewStep>({ type: 'settle', key, call, result }),
  dispose: (key: string) => void send({ type: 'dispose', key }).catch(() => undefined),
};
